import React, { useState } from 'react';
import {
  Brain,
  Bot,
  Sparkles as SparklesIcon,
  Wand2,
  Zap,
  Lightbulb,
  TrendingUp,
  BarChart3,
  PieChart,
  Calculator,
  Banknote,
  CreditCard,
  Scale,
  Gavel,
  Shield,
  FileCheck,
  ClipboardCheck,
  FileText,
  MessageSquare,
  BookOpen,
  Briefcase,
  Mail,
  Users,
  Megaphone,
  Target,
  Rocket,
  PenTool,
  Code,
  Database,
  Globe,
  Search,
  Headphones,
  Phone,
  UserCheck,
  Languages,
  Building2,
  GraduationCap,
  HeartPulse,
  Stethoscope,
  type LucideIcon,
} from 'lucide-react';
import { authService } from '../../services/auth';
import type { MyScenario } from '../../services/scenario';
import { getBundledScenarioAvatar } from './scenarioAvatarAssets';

const PRESET_ICON_MAP: Record<string, LucideIcon> = {
  Brain,
  Bot,
  Sparkles: SparklesIcon,
  Wand2,
  Zap,
  Lightbulb,
  TrendingUp,
  BarChart3,
  PieChart,
  Calculator,
  Banknote,
  CreditCard,
  Scale,
  Gavel,
  Shield,
  FileCheck,
  ClipboardCheck,
  FileText,
  MessageSquare,
  BookOpen,
  Briefcase,
  Mail,
  Users,
  Megaphone,
  Target,
  Rocket,
  PenTool,
  Code,
  Database,
  Globe,
  Search,
  Headphones,
  Phone,
  UserCheck,
  Languages,
  Building2,
  GraduationCap,
  HeartPulse,
  Stethoscope,
};

/** 解析图片地址，保留历史无前导斜杠上传路径；不将未知协议当成图片请求。 */
function imageUrl(icon: string): string | null {
  if (/^https?:\/\//i.test(icon)) return icon;
  if (/^(?:\/?uploads\/|\/?api\/)/i.test(icon)) {
    return `${authService.getBaseUrl().replace(/\/$/, '')}/${icon.replace(/^\//, '')}`;
  }
  return null;
}

/** 图片错误仅属于当前候选地址；切换平台/素材时由 key 重建，不保留跨账号全局失败缓存。 */
function ScenarioImage({
  primary,
  bundled,
  size,
  className,
  fallback,
}: {
  primary: string | null;
  bundled: string | null;
  size: number;
  className: string;
  fallback: React.ReactNode;
}) {
  const [failed, setFailed] = useState<Set<string>>(() => new Set());
  const source = [primary, bundled].find((candidate) => candidate && !failed.has(candidate));
  if (!source) return <>{fallback}</>;
  return (
    <img
      src={source}
      alt=""
      referrerPolicy="no-referrer"
      className={`object-cover ${className}`}
      style={{ width: size, height: size }}
      onError={() => setFailed((previous) => new Set(previous).add(source))}
    />
  );
}

/** 场景列表、详情、准备弹窗和输入框共用头像；平台上传优先，失败回退 BoMate 内置素材。 */
const ScenarioAppIcon: React.FC<{ scenario: MyScenario; size?: number; className?: string }> = ({
  scenario,
  size = 24,
  className = '',
}) => {
  const icon = scenario.icon?.trim() || '';
  const primary = imageUrl(icon);
  const bundled = getBundledScenarioAvatar(scenario);
  const fallback = (
    <span className={`leading-none ${className}`} style={{ fontSize: size * 0.85 }}>
      🤖
    </span>
  );

  if (primary || bundled)
    return (
      <ScenarioImage
        key={JSON.stringify([primary, bundled])}
        primary={primary}
        bundled={bundled}
        size={size}
        className={className}
        fallback={fallback}
      />
    );

  if (icon.startsWith('preset:')) {
    const IconComp = PRESET_ICON_MAP[icon.replace('preset:', '')];
    if (IconComp) {
      return <IconComp size={size} className={`text-blue-500 ${className}`} />;
    }
  }

  if (icon && !icon.startsWith('preset:') && !/^[a-z][a-z\d+.-]*:/i.test(icon)) {
    return (
      <span className={`leading-none ${className}`} style={{ fontSize: size * 0.85 }}>
        {icon}
      </span>
    );
  }

  return fallback;
};

export default ScenarioAppIcon;
